import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaDumbbell, FaCalendarAlt, FaUsers, FaCreditCard, FaPlus, FaEdit, FaTrash, FaEye, FaChartLine, FaPaperPlane, FaCopy, FaLink, FaRunning, FaSearch, FaFilter, FaTimes, FaGlobe, FaBoxOpen, FaCheck, FaCog, FaTachometerAlt, FaInfoCircle, FaUtensils, FaCommentDots } from 'react-icons/fa';
import { getCoachWorkouts, deleteCoachWorkout, publishCoachWorkout, getCoachPrograms, deleteCoachProgram, publishCoachProgram, getCoachFitnessStats, getCoachExercises, deleteCoachExercise, publishCoachExercise } from '../services/coachFitnessService';
import { getUnreadCounts } from '../services/messageService';
import toast from 'react-hot-toast';
import ChatPanel from './ChatPanel';
import ConfirmModal from './ConfirmModal';
import { NutritionPlannerWizard } from './NutritionPlanner';
import { useWeightUnit } from '../hooks/useWeightUnit';
import { lbsToDisplayWeight } from '../utils/weightUnits';
import './CoachDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export default function CoachDashboard() {
  const navigate = useNavigate();
  const weightUnit = useWeightUnit();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [workoutSearch, setWorkoutSearch] = useState('');
  const [workoutCategory, setWorkoutCategory] = useState('');
  const [workoutDifficulty, setWorkoutDifficulty] = useState('');
  const [workoutStatus, setWorkoutStatus] = useState('');
  const [showWorkoutFilters, setShowWorkoutFilters] = useState(false);
  const [programSearch, setProgramSearch] = useState('');
  const [programDifficulty, setProgramDifficulty] = useState('');
  const [programStatus, setProgramStatus] = useState('');
  const [showProgramFilters, setShowProgramFilters] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [addingClient, setAddingClient] = useState(false);
  const [sendInviteEmail, setSendInviteEmail] = useState(true);
  const [personalNote, setPersonalNote] = useState('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // Stripe Connect
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Services
  const serviceFormRef = useRef(null);
  const [services, setServices] = useState([]);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '', description: '', tier: 'custom', features: [''],
    sessionFrequency: 'weekly', sessionDuration: 60, sessionType: 'virtual',
    includesCustomProgram: false, linkedPrograms: [],
    price: '', billingInterval: 'monthly', visibility: 'public', maxClients: 0
  });
  const [savingService, setSavingService] = useState(false);

  // Usage & subscription
  const [usageStats, setUsageStats] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Content assignment
  const [basePrograms, setBasePrograms] = useState([]);
  const [assigningClient, setAssigningClient] = useState(null);
  const [assignType, setAssignType] = useState('program');
  const [assignContentId, setAssignContentId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [managingClient, setManagingClient] = useState(null);
  const [clientSchedule, setClientSchedule] = useState([]);
  const [clientSessions, setClientSessions] = useState([]);
  const [clientNutritionPlans, setClientNutritionPlans] = useState([]);
  const [viewingClientInfo, setViewingClientInfo] = useState(null);
  const [nutritionPlannerClient, setNutritionPlannerClient] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => localStorage.getItem('coach_onboarding_dismissed') === 'true');

  // Messaging
  const [messagingClient, setMessagingClient] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});

  // Confirm-modal state (replaces window.confirm)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', danger: false, confirmLabel: 'Confirm' });
  const confirmResolveRef = useRef(null);

  const openConfirm = ({ title = 'Confirm', message, danger = false, confirmLabel = danger ? 'Delete' : 'Confirm' }) =>
    new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmModal({ isOpen: true, title, message, danger, confirmLabel });
    });

  const closeConfirm = (result) => {
    confirmResolveRef.current?.(result);
    confirmResolveRef.current = null;
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  const clientOverview = useMemo(() => {
    return {
      total: clients.length,
      active: clients.filter((c) => c.status === 'active').length,
      invited: clients.filter((c) => c.status === 'invited').length,
      pending: clients.filter((c) => c.status === 'pending').length,
      withNutritionPlans: clients.filter((c) => c.activeNutritionGoalId).length,
      fitnessClients: clients.filter((c) => c.coachType === 'fitness' || c.coachType === 'both').length,
      nutritionClients: clients.filter((c) => c.coachType === 'nutrition' || c.coachType === 'both').length
    };
  }, [clients]);

  const onboardingChecklist = useMemo(() => {
    const items = [
      {
        id: 'stripe',
        label: 'Set up Stripe to accept payments',
        hint: 'Connect your Stripe account so clients can pay for your services.',
        done: !!stripeStatus?.chargesEnabled,
        action: () => setActiveTab('payments'),
        actionLabel: 'Go to Payments'
      },
      {
        id: 'service',
        label: 'Create a service package',
        hint: 'Define what you offer — pricing, session details, and features clients can subscribe to.',
        done: services.length > 0,
        action: () => setActiveTab('services'),
        actionLabel: 'Go to Services'
      },
      {
        id: 'clients',
        label: 'Add clients',
        hint: 'Invite clients to your practice so you can assign them workouts, programs, and nutrition plans.',
        done: clients.length > 0,
        action: () => setActiveTab('clients'),
        actionLabel: 'Manage Clients'
      },
      {
        id: 'workout',
        label: 'Create a workout (or browse stock ones)',
        hint: 'Build custom workouts for your clients, or start from our library of ready-made workouts.',
        done: workouts.length > 0,
        action: () => navigate('/coach/workout/create'),
        actionLabel: 'Create Workout'
      },
      {
        id: 'program',
        label: 'Create a program (or use stock programs)',
        hint: 'Organize workouts into multi-day/week programs. You can also assign existing programs to clients.',
        done: programs.length > 0,
        action: () => navigate('/coach/program/create'),
        actionLabel: 'Create Program'
      },
      {
        id: 'exercise',
        label: 'Create a custom exercise',
        hint: 'Add your own exercises with descriptions and instructions, or use our built-in exercise library.',
        done: exercises.length > 0,
        action: () => navigate('/coach/exercise/create'),
        actionLabel: 'Create Exercise'
      }
    ];
    const completedCount = items.filter(i => i.done).length;
    return { items, completedCount, total: items.length, allDone: completedCount === items.length };
  }, [stripeStatus, services, clients, workouts, programs, exercises, navigate, setActiveTab]);

  const handleDismissOnboarding = () => {
    setOnboardingDismissed(true);
    localStorage.setItem('coach_onboarding_dismissed', 'true');
  };

  const loadStripeStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/stripe-connect/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setStripeStatus(await res.json());
      }
    } catch (e) {
      // Stripe Connect may not be configured in every environment.
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Unread message counts polling
  const refreshUnreadCounts = async () => {
    try {
      const counts = await getUnreadCounts();
      setUnreadCounts(counts || {});
    } catch { /* silent */ }
  };

  useEffect(() => {
    refreshUnreadCounts();
    const interval = setInterval(refreshUnreadCounts, 60000);
    const onFocus = () => refreshUnreadCounts();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const totalUnread = Object.values(unreadCounts).reduce((sum, n) => sum + n, 0);

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsData, workoutsData, programsData, exercisesData] = await Promise.all([
        getCoachFitnessStats().catch(() => null),
        getCoachWorkouts().catch(() => ({ workouts: [] })),
        getCoachPrograms().catch(() => ({ programs: [] })),
        getCoachExercises().catch(() => ({ exercises: [] }))
      ]);
      setStats(statsData);
      setWorkouts(workoutsData.workouts || []);
      setPrograms(programsData.programs || []);
      setExercises(exercisesData.exercises || []);

      // Load Stripe status
      await loadStripeStatus();

      // Load clients
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/coach/clients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setClients(await res.json());
      } catch (e) { /* No clients */ }

      // Load services
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/coach-services`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setServices(await res.json());
      } catch (e) { /* No services */ }

      // Load base/system programs for assignment
      try {
        const res = await fetch(`${API_URL}/fitness/programs?isPublic=true`);
        if (res.ok) {
          const all = await res.json();
          const coachIds = new Set((programsData.programs || []).map(p => p._id));
          setBasePrograms(all.filter(p => !coachIds.has(p._id)));
        }
      } catch (e) { /* No base programs */ }

      // Load usage stats and subscription info
      try {
        const token = localStorage.getItem('token');
        const [usageRes, subRes] = await Promise.all([
          fetch(`${API_URL}/affiliate/usage-stats`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_URL}/subscription/status`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => null)
        ]);
        if (usageRes?.ok) setUsageStats(await usageRes.json());
        if (subRes?.ok) setSubscriptionInfo(await subRes.json());
      } catch (e) { /* Usage data not critical */ }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  // Stripe handlers
  const handleStripeOnboard = async () => {
    setStripeLoading(true);
    try {
      const res = await fetch(`${API_URL}/stripe-connect/onboard`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const { url } = await res.json();
        window.location.href = url;
      } else {
        const err = await res.json();
        toast.error(err.error || 'Failed to start onboarding');
      }
    } catch (e) {
      toast.error('Failed to connect Stripe');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/stripe-connect/dashboard-link`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.open(data.url, '_blank');
        } else {
          toast.success(data.message || 'Payments are managed by the platform automatically.');
        }
      }
    } catch (e) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const handleStripeRefresh = async () => {
    setStripeLoading(true);
    try {
      await loadStripeStatus();
      toast.success('Stripe status refreshed');
    } catch (e) {
      toast.error('Failed to refresh Stripe status');
    } finally {
      setStripeLoading(false);
    }
  };

  // Workout actions
  const handleDeleteWorkout = async (id) => {
    if (!(await openConfirm({ title: 'Delete Workout', message: 'Are you sure you want to delete this workout? This cannot be undone.', danger: true }))) return;
    try {
      await deleteCoachWorkout(id);
      setWorkouts(prev => prev.filter(w => w._id !== id));
      toast.success('Workout deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePublishWorkout = async (id) => {
    try {
      await publishCoachWorkout(id);
      setWorkouts(prev => prev.map(w => w._id === id ? { ...w, moderation_status: 'PENDING', visibility: 'public' } : w));
      toast.success('Workout submitted for review');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Program actions
  const handleDeleteProgram = async (id) => {
    if (!(await openConfirm({ title: 'Delete Program', message: 'Are you sure you want to delete this program? This cannot be undone.', danger: true }))) return;
    try {
      await deleteCoachProgram(id);
      setPrograms(prev => prev.filter(p => p._id !== id));
      toast.success('Program deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePublishProgram = async (id) => {
    try {
      const result = await publishCoachProgram(id);
      setPrograms(prev => prev.map(p => p._id === id ? { ...p, ...result.program } : p));
      toast.success(result.message || 'Program published');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePromoteProgram = async (id) => {
    if (!(await openConfirm({ title: 'Promote Program', message: 'Promote this invite-only program to public? It will be submitted for admin review.', confirmLabel: 'Promote' }))) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach-fitness/programs/${id}/promote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to promote');
      setPrograms(prev => prev.map(p => p._id === id ? { ...p, ...data.program } : p));
      toast.success(data.message || 'Program submitted for public review');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Exercise actions
  const handleDeleteExercise = async (id) => {
    if (!(await openConfirm({ title: 'Delete Exercise', message: 'Are you sure you want to delete this exercise? This cannot be undone.', danger: true }))) return;
    try {
      await deleteCoachExercise(id);
      setExercises(prev => prev.filter(e => e._id !== id));
      toast.success('Exercise deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePublishExercise = async (id) => {
    try {
      await publishCoachExercise(id);
      setExercises(prev => prev.map(e => e._id === id ? { ...e, moderation_status: 'PENDING', visibility: 'public' } : e));
      toast.success('Exercise submitted for review');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Client actions
  const handleAddClient = async (e) => {
    e.preventDefault();
    if (!newClientName.trim() || !newClientEmail.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setAddingClient(true);
    try {
      const res = await fetch(`${API_URL}/coach/clients`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          clientName: newClientName.trim(),
          clientEmail: newClientEmail.trim(),
          sendInviteEmail,
          personalNote: personalNote.trim() || undefined
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add client');
      setClients(prev => [data.client, ...prev]);
      setNewClientName('');
      setNewClientEmail('');
      setPersonalNote('');
      setShowAddClient(false);
      if (data.emailSent) {
        toast.success('Client added and invite email sent!');
      } else {
        toast.success('Client added! Share the invite link with them.');
      }
      // Copy invite link to clipboard
      if (data.inviteUrl) {
        navigator.clipboard.writeText(data.inviteUrl).then(() => {
          toast.success('Invite link copied to clipboard!');
        }).catch(() => {});
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddingClient(false);
    }
  };

  const copyInviteLink = (client) => {
    const url = `${window.location.origin}/coach/invite/${client.inviteToken}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Invite link copied!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const handlePreviewEmail = async () => {
    try {
      const params = new URLSearchParams();
      if (newClientName) params.set('clientName', newClientName);
      if (personalNote) params.set('personalNote', personalNote);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`${API_URL}/affiliate/invite-preview${qs}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load preview');
      setEmailPreviewHtml(data.html);
      setShowEmailPreview(!showEmailPreview);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!(await openConfirm({ title: 'Remove Client', message: `Remove ${clientName || 'this client'} from your client list?`, danger: true, confirmLabel: 'Remove' }))) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/clients/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove client');
      setClients(prev => prev.filter(c => c._id !== clientId));
      toast.success('Client removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleApproveClient = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'active' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to approve');
      setClients(prev => prev.map(c => c._id === clientId ? { ...c, status: 'active' } : c));
      toast.success('Client approved!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeclineClient = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/clients/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to decline');
      setClients(prev => prev.filter(c => c._id !== clientId));
      toast.success('Connection declined');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleManageClient = async (clientId) => {
    if (managingClient === clientId) {
      setManagingClient(null);
      setClientSchedule([]);
      setClientSessions([]);
      setClientNutritionPlans([]);
      return;
    }
    setManagingClient(clientId);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [scheduleRes, progressRes, nutritionRes] = await Promise.all([
        fetch(`${API_URL}/coach-fitness/clients/${clientId}/schedule`, { headers }),
        fetch(`${API_URL}/coach-fitness/clients/${clientId}/progress`, { headers }),
        fetch(`${API_URL}/coach/clients/${clientId}/nutrition-plans`, { headers })
      ]);
      if (scheduleRes.ok) setClientSchedule(await scheduleRes.json());
      if (progressRes.ok) {
        const progressData = await progressRes.json();
        setClientSessions(progressData.sessions || []);
      }
      if (nutritionRes.ok) {
        const nutritionData = await nutritionRes.json();
        setClientNutritionPlans(nutritionData.plans || nutritionData || []);
      }
    } catch (err) {
      console.error('Failed to load client data:', err);
    }
  };

  const handleResendInvite = async (clientId) => {
    try {
      const res = await fetch(`${API_URL}/coach/clients/${clientId}/resend-invite`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add client');
      // Update the client's invite token
      setClients(prev => prev.map(c => c._id === clientId ? { ...c, inviteToken: data.inviteUrl.split('/').pop() } : c));
      if (data.inviteUrl) {
        navigator.clipboard.writeText(data.inviteUrl).then(() => {
          toast.success('New invite link copied to clipboard!');
        }).catch(() => {
          toast.success('Invite regenerated');
        });
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const labels = { DRAFT: 'Draft', PENDING: 'Pending Review', APPROVED: 'Approved', REJECTED: 'Rejected' };
    const classes = { DRAFT: 'draft', PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected' };
    return <span className={`status-badge ${classes[status] || 'draft'}`}>{labels[status] || 'Draft'}</span>;
  };

  const workoutFilterOptions = useMemo(() => ({
    categories: [...new Set(workouts.map(workout => workout.category).filter(Boolean))].sort(),
    difficulties: [...new Set(workouts.map(workout => workout.difficulty).filter(Boolean))].sort()
  }), [workouts]);

  const programFilterOptions = useMemo(() => ({
    difficulties: [...new Set(programs.map(program => program.difficulty).filter(Boolean))].sort()
  }), [programs]);

  const normalizedWorkoutSearch = workoutSearch.trim().toLowerCase();
  const normalizedProgramSearch = programSearch.trim().toLowerCase();
  const hasWorkoutFilters = Boolean(normalizedWorkoutSearch || workoutCategory || workoutDifficulty || workoutStatus);
  const hasProgramFilters = Boolean(normalizedProgramSearch || programDifficulty || programStatus);

  const filteredWorkouts = useMemo(() => workouts.filter(workout => {
    const matchesSearch = !normalizedWorkoutSearch
      || workout.name?.toLowerCase().includes(normalizedWorkoutSearch)
      || workout.description?.toLowerCase().includes(normalizedWorkoutSearch);
    const matchesCategory = !workoutCategory || workout.category === workoutCategory;
    const matchesDifficulty = !workoutDifficulty || workout.difficulty === workoutDifficulty;
    const matchesStatus = !workoutStatus || workout.moderation_status === workoutStatus;
    return matchesSearch && matchesCategory && matchesDifficulty && matchesStatus;
  }), [normalizedWorkoutSearch, workoutCategory, workoutDifficulty, workoutStatus, workouts]);

  const filteredPrograms = useMemo(() => programs.filter(program => {
    const matchesSearch = !normalizedProgramSearch
      || program.name?.toLowerCase().includes(normalizedProgramSearch)
      || program.description?.toLowerCase().includes(normalizedProgramSearch);
    const matchesDifficulty = !programDifficulty || program.difficulty === programDifficulty;
    const matchesStatus = !programStatus || program.moderation_status === programStatus;
    return matchesSearch && matchesDifficulty && matchesStatus;
  }), [normalizedProgramSearch, programDifficulty, programStatus, programs]);

  const clearWorkoutFilters = () => {
    setWorkoutSearch('');
    setWorkoutCategory('');
    setWorkoutDifficulty('');
    setWorkoutStatus('');
  };

  const clearProgramFilters = () => {
    setProgramSearch('');
    setProgramDifficulty('');
    setProgramStatus('');
  };

  // Dev-only: instant Stripe test account
  const handleDevStripeOnboard = async () => {
    setStripeLoading(true);
    try {
      const res = await fetch(`${API_URL}/stripe-connect/dev-onboard`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Test Stripe account created!');
        await loadStripeStatus();
      } else {
        toast.error(data.error || 'Failed to create test account');
      }
    } catch (e) {
      toast.error('Failed to create test Stripe account');
    } finally {
      setStripeLoading(false);
    }
  };

  // Service handlers
  const resetServiceForm = () => {
    setServiceForm({
      name: '', description: '', tier: 'custom', features: [''],
      sessionFrequency: 'weekly', sessionDuration: 60, sessionType: 'virtual',
      includesCustomProgram: false, linkedPrograms: [],
      price: '', billingInterval: 'monthly', visibility: 'public', maxClients: 0
    });
    setEditingService(null);
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    if (!serviceForm.name.trim() || !serviceForm.price) {
      toast.error('Name and price are required');
      return;
    }
    setSavingService(true);
    try {
      const payload = {
        ...serviceForm,
        features: serviceForm.features.filter(f => f.trim()),
        price: Math.round(parseFloat(serviceForm.price) * 100),
      };
      const url = editingService
        ? `${API_URL}/coach-services/${editingService._id}`
        : `${API_URL}/coach-services`;
      const method = editingService ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save service');
      if (editingService) {
        setServices(prev => prev.map(s => s._id === data._id ? data : s));
        toast.success('Service updated');
      } else {
        setServices(prev => [data, ...prev]);
        toast.success('Service created');
      }
      resetServiceForm();
      setShowServiceForm(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingService(false);
    }
  };

  const handleEditService = (service) => {
    setServiceForm({
      name: service.name || '',
      description: service.description || '',
      tier: service.tier || 'custom',
      features: service.features?.length ? [...service.features] : [''],
      sessionFrequency: service.sessionFrequency || 'weekly',
      sessionDuration: service.sessionDuration || 60,
      sessionType: service.sessionType || 'virtual',
      includesCustomProgram: !!service.includesCustomProgram,
      linkedPrograms: service.linkedPrograms?.map(p => p._id || p) || [],
      price: service.price ? (service.price / 100).toFixed(2) : '',
      billingInterval: service.billingInterval || 'monthly',
      visibility: service.visibility || 'public',
      maxClients: service.maxClients || 0
    });
    setEditingService(service);
    setShowServiceForm(true);
  };

  useEffect(() => {
    if (editingService && serviceFormRef.current) {
      serviceFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingService]);

  const handleDeleteService = async (id) => {
    if (!(await openConfirm({ title: 'Deactivate Service', message: 'Are you sure you want to deactivate this service?', danger: true, confirmLabel: 'Deactivate' }))) return;
    try {
      const res = await fetch(`${API_URL}/coach-services/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to delete');
      setServices(prev => prev.filter(s => s._id !== id));
      toast.success('Service deactivated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const updateServiceFeature = (index, value) => {
    setServiceForm(prev => {
      const features = [...prev.features];
      features[index] = value;
      return { ...prev, features };
    });
  };

  const addServiceFeature = () => {
    setServiceForm(prev => ({ ...prev, features: [...prev.features, ''] }));
  };

  const removeServiceFeature = (index) => {
    setServiceForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  const handleUnassign = async (clientId, assignmentId) => {
    if (!(await openConfirm({ title: 'Remove Assignment', message: 'Remove this assignment from the client?', danger: true, confirmLabel: 'Remove' }))) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach-fitness/clients/${clientId}/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to unassign');
      }
      toast.success('Assignment removed');
      setClientSchedule(prev => prev.filter(s => s._id !== assignmentId));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAssignContent = async (clientId) => {
    if (!assignContentId) {
      toast.error('Select something to assign');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const body = {
        type: assignType,
        notes: assignNotes || ''
      };
      if (assignType === 'program') body.programId = assignContentId;
      else if (assignType === 'workout') body.workoutId = assignContentId;

      const res = await fetch(`${API_URL}/coach-fitness/clients/${clientId}/assign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to assign');
      toast.success(`${assignType === 'program' ? 'Program' : 'Workout'} assigned to client!`);
      setAssigningClient(null);
      setAssignContentId('');
      setAssignNotes('');
      handleManageClient(clientId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return (
      <div className="coach-dashboard">
        <div className="coach-loading">
          <div className="loading-spinner" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FaChartLine /> },
    { id: 'workouts', label: 'Workouts', icon: <FaDumbbell /> },
    { id: 'programs', label: 'Programs', icon: <FaCalendarAlt /> },
    { id: 'exercises', label: 'Exercises', icon: <FaRunning /> },
    { id: 'services', label: 'Services', icon: <FaBoxOpen /> },
    { id: 'clients', label: totalUnread > 0 ? `Clients (${totalUnread})` : 'Clients', icon: <FaUsers /> },
    { id: 'usage', label: 'Usage', icon: <FaTachometerAlt /> },
    { id: 'payments', label: 'Payments', icon: <FaCreditCard /> }
  ];

  const stripeActionItems = Array.from(new Set([
    ...(stripeStatus?.requirementsCurrentlyDue || []),
    ...(stripeStatus?.requirementsPastDue || [])
  ]));

  const stripeAccountTypeLabel = stripeStatus?.accountType
    ? `${stripeStatus.accountType.charAt(0).toUpperCase()}${stripeStatus.accountType.slice(1)} account`
    : 'Stripe account';

  return (
    <div className="coach-dashboard">
      <div className="coach-dashboard-container">
        <div className="coach-dashboard-header">
          <h1>Coach Dashboard</h1>
          <p>Manage your clients, fitness content, nutrition plans, and payments</p>
        </div>

        {/* Tab Navigation */}
        <div className="coach-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`coach-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon"><FaDumbbell /></div>
                <div className="stat-value">{workouts.length}</div>
                <div className="stat-label">Workouts</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaCalendarAlt /></div>
                <div className="stat-value">{programs.length}</div>
                <div className="stat-label">Programs</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaRunning /></div>
                <div className="stat-value">{exercises.length}</div>
                <div className="stat-label">Exercises</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaUsers /></div>
                <div className="stat-value">{clients.length}</div>
                <div className="stat-label">Clients</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaEye /></div>
                <div className="stat-value">{stats?.totalViews || 0}</div>
                <div className="stat-label">Total Views</div>
              </div>
            </div>

            {/* Onboarding Checklist */}
            {!onboardingDismissed && !loading && (
              <div className={`onboarding-checklist ${onboardingChecklist.allDone ? 'all-done' : ''}`}>
                <div className="onboarding-header">
                  <div>
                    <h3>🚀 Getting Started</h3>
                    <p className="onboarding-progress-text">
                      {onboardingChecklist.allDone
                        ? "You're all set! You've completed every step."
                        : `${onboardingChecklist.completedCount} of ${onboardingChecklist.total} steps complete`}
                    </p>
                  </div>
                  <button className="onboarding-dismiss" onClick={handleDismissOnboarding} title="Dismiss">
                    <FaTimes />
                  </button>
                </div>
                <div className="onboarding-progress-bar">
                  <div
                    className="onboarding-progress-fill"
                    style={{ width: `${(onboardingChecklist.completedCount / onboardingChecklist.total) * 100}%` }}
                  />
                </div>
                <ul className="onboarding-items">
                  {onboardingChecklist.items.map(item => (
                    <li key={item.id} className={`onboarding-item ${item.done ? 'done' : ''}`}>
                      <span className="onboarding-check">
                        {item.done ? <FaCheck /> : <span className="onboarding-circle" />}
                      </span>
                      <div className="onboarding-item-content">
                        <span className="onboarding-item-label">{item.label}</span>
                        {!item.done && <span className="onboarding-item-hint">{item.hint}</span>}
                      </div>
                      {!item.done && (
                        <button className="onboarding-item-action" onClick={item.action}>
                          {item.actionLabel}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="quick-actions">
              <h3>Quick Actions</h3>
              <div className="quick-actions-grid">
                <button onClick={() => navigate('/coach/workout/create')} className="quick-action-btn">
                  <FaPlus /> Create Workout
                </button>
                <button onClick={() => navigate('/coach/program/create')} className="quick-action-btn">
                  <FaPlus /> Create Program
                </button>
                <button onClick={() => navigate('/coach/exercise/create')} className="quick-action-btn">
                  <FaPlus /> Create Exercise
                </button>
                <button onClick={() => setActiveTab('clients')} className="quick-action-btn secondary">
                  <FaUsers /> Manage Clients
                </button>
                <button onClick={() => setActiveTab('payments')} className="quick-action-btn secondary">
                  <FaCreditCard /> Payment Settings
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            {(workouts.length > 0 || programs.length > 0) && (
              <div className="recent-activity">
                <h3>Recent Content</h3>
                <div className="activity-list">
                  {[...workouts.slice(0, 3).map(w => ({ ...w, type: 'workout' })), ...programs.slice(0, 3).map(p => ({ ...p, type: 'program' }))]
                    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                    .slice(0, 5)
                    .map(item => (
                      <div key={item._id} className="activity-item">
                        <div className="activity-icon">
                          {item.type === 'workout' ? <FaDumbbell /> : <FaCalendarAlt />}
                        </div>
                        <div className="activity-details">
                          <span className="activity-name">{item.name}</span>
                          <span className="activity-meta">{item.type} · {getStatusBadge(item.moderation_status)}</span>
                        </div>
                        <span className="activity-date">{new Date(item.updatedAt).toLocaleDateString()}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Workouts Tab */}
        {activeTab === 'workouts' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>My Workouts</h2>
              <button className="create-btn" onClick={() => navigate('/coach/workout/create')}>
                <FaPlus /> New Workout
              </button>
            </div>

            <div className="dashboard-search-filter-section">
              <div className="dashboard-search-box">
                <FaSearch className="dashboard-search-icon" />
                <input
                  type="text"
                  placeholder="Search workouts..."
                  value={workoutSearch}
                  onChange={(e) => setWorkoutSearch(e.target.value)}
                />
                {workoutSearch && (
                  <button className="dashboard-clear-search" onClick={() => setWorkoutSearch('')}>
                    <FaTimes />
                  </button>
                )}
              </div>

              <button
                className={`dashboard-filter-toggle ${showWorkoutFilters ? 'active' : ''}`}
                onClick={() => setShowWorkoutFilters(!showWorkoutFilters)}
              >
                <FaFilter />
                <span>Filters</span>
                {hasWorkoutFilters && <span className="dashboard-filter-badge" />}
              </button>
            </div>

            {showWorkoutFilters && (
              <div className="dashboard-filters-panel">
                <div className="dashboard-filter-group">
                  <label>Category</label>
                  <select value={workoutCategory} onChange={(e) => setWorkoutCategory(e.target.value)}>
                    <option value="">All categories</option>
                    {workoutFilterOptions.categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="dashboard-filter-group">
                  <label>Difficulty</label>
                  <select value={workoutDifficulty} onChange={(e) => setWorkoutDifficulty(e.target.value)}>
                    <option value="">All difficulties</option>
                    {workoutFilterOptions.difficulties.map(difficulty => (
                      <option key={difficulty} value={difficulty}>{difficulty}</option>
                    ))}
                  </select>
                </div>

                <div className="dashboard-filter-group">
                  <label>Status</label>
                  <select value={workoutStatus} onChange={(e) => setWorkoutStatus(e.target.value)}>
                    <option value="">All statuses</option>
                    <option value="DRAFT">Draft</option>
                    <option value="PENDING">Pending Review</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>

                {hasWorkoutFilters && (
                  <button className="dashboard-clear-filters" onClick={clearWorkoutFilters}>
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            {workouts.length === 0 ? (
              <div className="empty-state">
                <FaDumbbell />
                <p>No workouts yet. Create your first workout!</p>
                <button onClick={() => navigate('/coach/workout/create')}><FaPlus /> Create Workout</button>
              </div>
            ) : filteredWorkouts.length === 0 ? (
              <div className="empty-state">
                <FaSearch />
                <p>No workouts match the current search and filters.</p>
                <button onClick={clearWorkoutFilters}>Clear Filters</button>
              </div>
            ) : (
              <div className="content-list">
                {filteredWorkouts.map(workout => (
                  <div key={workout._id} className="content-card">
                    <div className="content-card-main">
                      <h4>{workout.name}</h4>
                      <div className="content-card-meta">
                        <span>{workout.difficulty}</span>
                        <span>{workout.estimatedDuration} min</span>
                        <span>{workout.category}</span>
                        {getStatusBadge(workout.moderation_status)}
                      </div>
                    </div>
                    <div className="content-card-actions">
                      {workout.workoutId && (
                        <button title="Open Workout" onClick={() => navigate(`/fitness/workout/${workout.workoutId}`)}>
                          <FaEye />
                        </button>
                      )}
                      <button title="Edit" onClick={() => navigate(`/coach/workout/${workout._id}/edit`)}>
                        <FaEdit />
                      </button>
                      {workout.moderation_status === 'DRAFT' && (
                        <button title="Submit for Review" onClick={() => handlePublishWorkout(workout._id)}>
                          <FaPaperPlane />
                        </button>
                      )}
                      <button title="Delete" className="danger" onClick={() => handleDeleteWorkout(workout._id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Programs Tab */}
        {activeTab === 'programs' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>My Programs</h2>
              <button className="create-btn" onClick={() => navigate('/coach/program/create')}>
                <FaPlus /> New Program
              </button>
            </div>

            <div className="dashboard-search-filter-section">
              <div className="dashboard-search-box">
                <FaSearch className="dashboard-search-icon" />
                <input
                  type="text"
                  placeholder="Search programs..."
                  value={programSearch}
                  onChange={(e) => setProgramSearch(e.target.value)}
                />
                {programSearch && (
                  <button className="dashboard-clear-search" onClick={() => setProgramSearch('')}>
                    <FaTimes />
                  </button>
                )}
              </div>

              <button
                className={`dashboard-filter-toggle ${showProgramFilters ? 'active' : ''}`}
                onClick={() => setShowProgramFilters(!showProgramFilters)}
              >
                <FaFilter />
                <span>Filters</span>
                {hasProgramFilters && <span className="dashboard-filter-badge" />}
              </button>
            </div>

            {showProgramFilters && (
              <div className="dashboard-filters-panel">
                <div className="dashboard-filter-group">
                  <label>Difficulty</label>
                  <select value={programDifficulty} onChange={(e) => setProgramDifficulty(e.target.value)}>
                    <option value="">All difficulties</option>
                    {programFilterOptions.difficulties.map(difficulty => (
                      <option key={difficulty} value={difficulty}>{difficulty}</option>
                    ))}
                  </select>
                </div>

                <div className="dashboard-filter-group">
                  <label>Status</label>
                  <select value={programStatus} onChange={(e) => setProgramStatus(e.target.value)}>
                    <option value="">All statuses</option>
                    <option value="DRAFT">Draft</option>
                    <option value="PENDING">Pending Review</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>

                {hasProgramFilters && (
                  <button className="dashboard-clear-filters" onClick={clearProgramFilters}>
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            {programs.length === 0 ? (
              <div className="empty-state">
                <FaCalendarAlt />
                <p>No programs yet. Create your first program!</p>
                <button onClick={() => navigate('/coach/program/create')}><FaPlus /> Create Program</button>
              </div>
            ) : filteredPrograms.length === 0 ? (
              <div className="empty-state">
                <FaSearch />
                <p>No programs match the current search and filters.</p>
                <button onClick={clearProgramFilters}>Clear Filters</button>
              </div>
            ) : (
              <div className="content-list">
                {filteredPrograms.map(program => (
                  <div key={program._id} className="content-card">
                    <div className="content-card-main">
                      <h4>{program.name}</h4>
                      <div className="content-card-meta">
                        <span>{program.difficulty}</span>
                        <span>{program.durationWeeks} weeks</span>
                        <span>{program.daysPerWeek} days/wk</span>
                        {program.accessType === 'invite_only' && (
                          <span className="access-badge invite-only">Invite Only</span>
                        )}
                        {getStatusBadge(program.moderation_status)}
                      </div>
                    </div>
                    <div className="content-card-actions">
                      {program.programId && (
                        <button title="Open Program" onClick={() => navigate(`/fitness/program/${program.programId}`)}>
                          <FaEye />
                        </button>
                      )}
                      <button title="Edit" onClick={() => navigate(`/coach/program/${program._id}/edit`)}>
                        <FaEdit />
                      </button>
                      {program.moderation_status === 'DRAFT' && (
                        <button
                          title={program.accessType === 'invite_only' ? 'Activate for Clients' : 'Submit for Review'}
                          onClick={() => handlePublishProgram(program._id)}
                        >
                          <FaPaperPlane />
                        </button>
                      )}
                      {program.accessType === 'invite_only' && program.moderation_status === 'APPROVED' && (
                        <button title="Promote to Public" onClick={() => handlePromoteProgram(program._id)}>
                          <FaGlobe />
                        </button>
                      )}
                      <button title="Delete" className="danger" onClick={() => handleDeleteProgram(program._id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Exercises Tab */}
        {activeTab === 'exercises' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>My Exercises</h2>
              <button className="create-btn" onClick={() => navigate('/coach/exercise/create')}>
                <FaPlus /> New Exercise
              </button>
            </div>

            {exercises.length === 0 ? (
              <div className="empty-state">
                <FaRunning />
                <p>No exercises yet. Create your first exercise!</p>
                <button onClick={() => navigate('/coach/exercise/create')}><FaPlus /> Create Exercise</button>
              </div>
            ) : (
              <div className="content-list">
                {exercises.map(exercise => (
                  <div key={exercise._id} className="content-card">
                    <div className="content-card-main">
                      <h4>{exercise.name}</h4>
                      <div className="content-card-meta">
                        <span>{exercise.difficulty}</span>
                        <span>{exercise.category}</span>
                        <span>{exercise.measurementType}</span>
                        {getStatusBadge(exercise.moderation_status)}
                      </div>
                    </div>
                    <div className="content-card-actions">
                      {exercise.exerciseId && (
                        <button title="Open Exercise" onClick={() => navigate(`/fitness/exercise/${exercise.exerciseId}`)}>
                          <FaEye />
                        </button>
                      )}
                      <button title="Edit" onClick={() => navigate(`/coach/exercise/${exercise._id}/edit`)}>
                        <FaEdit />
                      </button>
                      {exercise.moderation_status === 'DRAFT' && (
                        <button title="Submit for Review" onClick={() => handlePublishExercise(exercise._id)}>
                          <FaPaperPlane />
                        </button>
                      )}
                      <button title="Delete" className="danger" onClick={() => handleDeleteExercise(exercise._id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>My Clients</h2>
            </div>

            <section className="coach-clients-overview-section" aria-labelledby="coach-clients-overview-heading">
              <div className="coach-clients-overview-header">
                <h3 id="coach-clients-overview-heading">
                  <FaUsers aria-hidden /> Clients overview
                </h3>
                <span className="coach-clients-overview-badge">
                  {clientOverview.active} active
                  {clientOverview.fitnessClients > 0 ? ` · ${clientOverview.fitnessClients} fitness clients` : ''}
                  {clientOverview.nutritionClients > 0 ? ` · ${clientOverview.nutritionClients} nutrition clients` : ''}
                </span>
              </div>

              <div className="coach-clients-overview-stats">
                <div className="coach-overview-stat-card">
                  <span className="coach-overview-stat-value">{clientOverview.total}</span>
                  <span className="coach-overview-stat-label">Total clients</span>
                </div>
                <div className="coach-overview-stat-card">
                  <span className="coach-overview-stat-value">{clientOverview.active}</span>
                  <span className="coach-overview-stat-label">Active</span>
                </div>
                <div className="coach-overview-stat-card">
                  <span className="coach-overview-stat-value">{clientOverview.invited}</span>
                  <span className="coach-overview-stat-label">Invited</span>
                </div>
                {clientOverview.pending > 0 && (
                  <div className="coach-overview-stat-card coach-overview-stat-pending">
                    <span className="coach-overview-stat-value">{clientOverview.pending}</span>
                    <span className="coach-overview-stat-label">Pending requests</span>
                  </div>
                )}
                <div className="coach-overview-stat-card coach-overview-stat-nutrition">
                  <span className="coach-overview-stat-value">{clientOverview.nutritionClients}</span>
                  <span className="coach-overview-stat-label">Nutrition</span>
                </div>
                <div className="coach-overview-stat-card coach-overview-stat-fitness">
                  <span className="coach-overview-stat-value">{clientOverview.fitnessClients}</span>
                  <span className="coach-overview-stat-label">Fitness</span>
                </div>
              </div>

              <div className="coach-clients-overview-help">
                <FaInfoCircle aria-hidden />
                <div>
                  <strong>How it works:</strong>
                  <ol>
                    <li>Click <strong>Add client</strong> below, then enter their name and email.</li>
                    <li>
                      For nutrition or combined clients, use the <FaUtensils aria-hidden className="coach-help-inline-icon" /> button to build or edit their meal plan.
                      For fitness, use <strong>+</strong> to assign programs or workouts and <FaCog aria-hidden className="coach-help-inline-icon" /> to track what they have.
                    </li>
                    <li>Copy or resend the invite link so they can connect, view plans, and shop.</li>
                  </ol>
                </div>
              </div>

              <div className="coach-add-client-row">
                <button type="button" className="add-btn" onClick={() => setShowAddClient(!showAddClient)}>
                  <FaPlus /> {showAddClient ? 'Cancel' : 'Add client'}
                </button>
              </div>
            </section>

            {showAddClient && (
              <form className="add-client-form" onSubmit={handleAddClient}>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Client name"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    required
                  />
                  <input
                    type="email"
                    placeholder="Client email"
                    value={newClientEmail}
                    onChange={(e) => setNewClientEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-row invite-options">
                  <label className="toggle-label">
                    <input type="checkbox" checked={sendInviteEmail} onChange={(e) => setSendInviteEmail(e.target.checked)} />
                    Send email invite
                  </label>
                  <button type="button" className="link-btn" onClick={handlePreviewEmail}>Preview Email</button>
                </div>
                {sendInviteEmail && (
                  <textarea
                    className="personal-note-input"
                    placeholder="Add a personal note (optional)"
                    value={personalNote}
                    onChange={(e) => setPersonalNote(e.target.value)}
                    maxLength={500}
                    rows={2}
                  />
                )}
                <div className="form-row">
                  <button type="submit" className="add-btn" disabled={addingClient}>
                    {addingClient ? 'Adding...' : 'Add Client'}
                  </button>
                </div>

                {showEmailPreview && emailPreviewHtml && (
                  <div className="email-preview-inline">
                    <div className="email-preview-inline-header">
                      <h4>Email Preview</h4>
                      <button type="button" className="link-btn" onClick={() => setShowEmailPreview(false)}><FaTimes /></button>
                    </div>
                    <div className="email-preview-body" dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
                  </div>
                )}
              </form>
            )}

            {/* Pending Connection Requests */}
            {clients.filter(c => c.status === 'pending').length > 0 && (
              <div className="pending-requests-section">
                <h3>🔔 Connection Requests</h3>
                <div className="pending-requests-list">
                  {clients.filter(c => c.status === 'pending').map(client => (
                    <div key={client._id} className="pending-request-card">
                      <div className="pending-request-info">
                        <h4>{client.clientName || client.clientEmail || 'User'}</h4>
                        {client.clientEmail && <span className="pending-email">{client.clientEmail}</span>}
                        {client.connectMessage && (
                          <p className="pending-message">"{client.connectMessage}"</p>
                        )}
                        <span className="pending-date">{client.createdAt ? new Date(client.createdAt).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="pending-request-actions">
                        <button className="approve-btn" onClick={() => handleApproveClient(client._id)} title="Approve">
                          <FaCheck /> Accept
                        </button>
                        <button className="decline-btn" onClick={() => handleDeclineClient(client._id)} title="Decline">
                          <FaTimes /> Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {clients.filter(c => c.status !== 'pending').length === 0 && clients.filter(c => c.status === 'pending').length === 0 && !showAddClient ? (
              <div className="empty-state">
                <FaUsers />
                <p>No clients yet. Click "Add Client" to invite your first client!</p>
              </div>
            ) : (
              <div className="content-list">
                {clients.filter(c => c.status !== 'pending').map(client => (
                  <div key={client._id} className="content-card client-card">
                    <div className="content-card-main">
                      <h4>{client.clientName || client.clientEmail || 'Client'}</h4>
                      <div className="content-card-meta">
                        <span className={`client-status ${client.status}`}>
                          {client.status === 'invited' ? '⏳ Invited' : client.status === 'active' ? '✅ Active' : client.status}
                        </span>
                        <span className="client-type">{client.coachType === 'both' ? 'Nutrition + Fitness' : client.coachType === 'fitness' ? 'Fitness' : 'Nutrition'}</span>
                        {client.createdAt && <span>{new Date(client.createdAt).toLocaleDateString()}</span>}
                      </div>
                      {client.fitnessProfile && (client.fitnessProfile.goals?.length > 0 || client.fitnessProfile.experienceLevel || client.fitnessProfile.availableEquipment?.length > 0) && (
                        <div className="client-fitness-profile">
                          {client.fitnessProfile.experienceLevel && (
                            <span className="profile-tag experience">{client.fitnessProfile.experienceLevel}</span>
                          )}
                          {client.fitnessProfile.goals?.map(g => (
                            <span key={g} className="profile-tag goal">{g.replace(/_/g, ' ')}</span>
                          ))}
                          {client.fitnessProfile.daysPerWeek && (
                            <span className="profile-tag schedule">{client.fitnessProfile.daysPerWeek}x/wk</span>
                          )}
                          {client.fitnessProfile.sessionMinutes && (
                            <span className="profile-tag schedule">{client.fitnessProfile.sessionMinutes}min</span>
                          )}
                          {client.fitnessProfile.availableEquipment?.length > 0 && (
                            <span className="profile-tag equipment" title={client.fitnessProfile.availableEquipment.join(', ')}>
                              {client.fitnessProfile.availableEquipment.length} equipment
                            </span>
                          )}
                          {client.fitnessProfile.hasGymAccess && (
                            <span className="profile-tag gym">🏋️ Gym</span>
                          )}
                        </div>
                      )}
                      {viewingClientInfo === client._id && (
                        <div className="client-info-panel">
                          {client.clientProfile && (client.clientProfile.age || client.clientProfile.gender || client.clientProfile.currentWeight) ? (
                            <div className="info-panel-section">
                              <h5>Demographics</h5>
                              <div className="info-panel-grid">
                                {client.clientProfile.age && <div className="info-item"><span className="info-label">Age</span><span className="info-value">{client.clientProfile.age}</span></div>}
                                {client.clientProfile.gender && <div className="info-item"><span className="info-label">Gender</span><span className="info-value">{client.clientProfile.gender}</span></div>}
                                {client.clientProfile.currentWeight && <div className="info-item"><span className="info-label">Current Weight</span><span className="info-value">{lbsToDisplayWeight(client.clientProfile.currentWeight, weightUnit).value} {weightUnit}</span></div>}
                                {client.clientProfile.goalWeight && <div className="info-item"><span className="info-label">Goal Weight</span><span className="info-value">{lbsToDisplayWeight(client.clientProfile.goalWeight, weightUnit).value} {weightUnit}</span></div>}
                                {client.clientProfile.height && <div className="info-item"><span className="info-label">Height</span><span className="info-value">{client.clientProfile.height} in</span></div>}
                                {client.clientProfile.activityLevel && <div className="info-item"><span className="info-label">Activity</span><span className="info-value">{client.clientProfile.activityLevel.replace(/_/g, ' ')}</span></div>}
                              </div>
                              {client.clientProfile.notes && <p className="info-notes">{client.clientProfile.notes}</p>}
                            </div>
                          ) : null}
                          {client.fitnessProfile && (
                            <div className="info-panel-section">
                              <h5>Fitness Profile</h5>
                              <div className="info-panel-grid">
                                {client.fitnessProfile.experienceLevel && <div className="info-item"><span className="info-label">Experience</span><span className="info-value">{client.fitnessProfile.experienceLevel}</span></div>}
                                {client.fitnessProfile.daysPerWeek && <div className="info-item"><span className="info-label">Days/Week</span><span className="info-value">{client.fitnessProfile.daysPerWeek}</span></div>}
                                {client.fitnessProfile.sessionMinutes && <div className="info-item"><span className="info-label">Session Length</span><span className="info-value">{client.fitnessProfile.sessionMinutes} min</span></div>}
                                {client.fitnessProfile.hasGymAccess != null && <div className="info-item"><span className="info-label">Gym Access</span><span className="info-value">{client.fitnessProfile.hasGymAccess ? 'Yes' : 'No'}</span></div>}
                              </div>
                              {client.fitnessProfile.goals?.length > 0 && (
                                <div className="info-tags-row"><span className="info-label">Goals:</span> {client.fitnessProfile.goals.map(g => <span key={g} className="profile-tag goal">{g.replace(/_/g, ' ')}</span>)}</div>
                              )}
                              {client.fitnessProfile.availableEquipment?.length > 0 && (
                                <div className="info-tags-row"><span className="info-label">Equipment:</span> {client.fitnessProfile.availableEquipment.map(e => <span key={e} className="profile-tag equipment">{e}</span>)}</div>
                              )}
                              {client.fitnessProfile.injuries?.length > 0 && (
                                <div className="info-tags-row"><span className="info-label">Injuries:</span> {client.fitnessProfile.injuries.map(i => <span key={i} className="profile-tag" style={{background: '#fee2e2', color: '#dc2626'}}>{i}</span>)}</div>
                              )}
                              {client.fitnessProfile.notes && <p className="info-notes">{client.fitnessProfile.notes}</p>}
                            </div>
                          )}
                          {client.nutritionProfile && (
                            <div className="info-panel-section">
                              <h5>Nutrition Preferences</h5>
                              <div className="info-panel-grid">
                                {client.nutritionProfile.goal && <div className="info-item"><span className="info-label">Goal</span><span className="info-value">{client.nutritionProfile.goal.replace(/_/g, ' ')}</span></div>}
                                {client.nutritionProfile.mealsPerDay && <div className="info-item"><span className="info-label">Meals/Day</span><span className="info-value">{client.nutritionProfile.mealsPerDay}</span></div>}
                                {client.nutritionProfile.cookingSkill && <div className="info-item"><span className="info-label">Cooking Skill</span><span className="info-value">{client.nutritionProfile.cookingSkill}</span></div>}
                                {client.nutritionProfile.budget && <div className="info-item"><span className="info-label">Budget</span><span className="info-value">{client.nutritionProfile.budget}</span></div>}
                              </div>
                              {client.nutritionProfile.dietaryRestrictions?.length > 0 && (
                                <div className="info-tags-row"><span className="info-label">Dietary:</span> {client.nutritionProfile.dietaryRestrictions.map(d => <span key={d} className="profile-tag">{d}</span>)}</div>
                              )}
                              {client.nutritionProfile.allergies?.length > 0 && (
                                <div className="info-tags-row"><span className="info-label">Allergies:</span> {client.nutritionProfile.allergies.map(a => <span key={a} className="profile-tag" style={{background: '#fee2e2', color: '#dc2626'}}>{a}</span>)}</div>
                              )}
                              {client.nutritionProfile.cuisinePreferences?.length > 0 && (
                                <div className="info-tags-row"><span className="info-label">Cuisines:</span> {client.nutritionProfile.cuisinePreferences.map(c => <span key={c} className="profile-tag">{c}</span>)}</div>
                              )}
                              {client.nutritionProfile.notes && <p className="info-notes">{client.nutritionProfile.notes}</p>}
                            </div>
                          )}
                          {!client.clientProfile?.age && !client.fitnessProfile && !client.nutritionProfile && (
                            <p className="info-empty">No profile information available yet. The client will fill this in when they accept their invite.</p>
                          )}
                        </div>
                      )}
                      {client.status === 'active' && assigningClient === client._id && (
                        <div className="assign-panel">
                          <div className="assign-type-toggle">
                            <button className={`assign-type-btn ${assignType === 'program' ? 'active' : ''}`} onClick={() => { setAssignType('program'); setAssignContentId(''); }}>
                              <FaDumbbell /> Program
                            </button>
                            <button className={`assign-type-btn ${assignType === 'workout' ? 'active' : ''}`} onClick={() => { setAssignType('workout'); setAssignContentId(''); }}>
                              <FaRunning /> Workout
                            </button>
                          </div>
                          <select value={assignContentId} onChange={e => setAssignContentId(e.target.value)}>
                            <option value="">Select a {assignType}...</option>
                            {assignType === 'program' ? (
                              <>
                                {programs.length > 0 && <optgroup label="Your Programs">
                                  {programs.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                </optgroup>}
                                {basePrograms.length > 0 && <optgroup label="Base Programs">
                                  {basePrograms.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                </optgroup>}
                              </>
                            ) : (
                              <>
                                {workouts.length > 0 && workouts.map(w => (
                                  <option key={w._id} value={w._id}>{w.name}</option>
                                ))}
                              </>
                            )}
                          </select>
                          <input
                            type="text"
                            placeholder="Add a note for your client (optional)"
                            value={assignNotes}
                            onChange={e => setAssignNotes(e.target.value)}
                            className="assign-notes-input"
                          />
                          <p className="assign-hint"><FaInfoCircle /> This will appear in your client's "My Workouts" — they can start it whenever they're ready.</p>
                          <div className="assign-actions">
                            <button className="assign-confirm-btn" onClick={() => handleAssignContent(client._id)}>
                              <FaPaperPlane /> Assign to Client
                            </button>
                            <button className="assign-cancel-btn" onClick={() => { setAssigningClient(null); setAssignContentId(''); setAssignNotes(''); }}>Cancel</button>
                          </div>
                        </div>
                      )}
                      {client.status === 'active' && managingClient === client._id && (
                        <div className="manage-client-panel">
                          {/* Activity Summary */}
                          {clientSessions.length > 0 && (
                            <div className="manage-activity-summary">
                              <span className="manage-activity-stat">
                                <strong>{clientSessions.filter(s => s.status === 'completed').length}</strong> completed
                              </span>
                              <span className="manage-activity-stat">
                                <strong>{clientSessions.filter(s => {
                                  if (!s.completedAt) return false;
                                  const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
                                  return new Date(s.completedAt) >= weekAgo;
                                }).length}</strong> this week
                              </span>
                              {clientSessions[0]?.completedAt && (
                                <span className="manage-activity-stat muted">
                                  Last: {new Date(clientSessions[0].completedAt || clientSessions[0].startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              )}
                            </div>
                          )}

                          <h5>Assigned Content</h5>
                          {clientSchedule.length === 0 ? (
                            <p className="manage-empty">Nothing assigned yet. Use the <strong>+</strong> button to assign a program or workout.</p>
                          ) : (
                            <div className="schedule-list">
                              {clientSchedule.map(item => (
                                <div key={item._id} className="schedule-item">
                                  <div className="schedule-item-info">
                                    <span className={`schedule-type-icon ${item.type}`}>
                                      {item.type === 'program' ? <FaDumbbell /> : item.type === 'workout' ? <FaRunning /> : <FaBoxOpen />}
                                    </span>
                                    <div>
                                      <span className="schedule-name">
                                        {item.programId?.name || item.workoutId?.name || item.exerciseId?.name || 'Activity'}
                                      </span>
                                      {item.scheduledDate && (
                                        <span className="schedule-date">{new Date(item.scheduledDate).toLocaleDateString()}</span>
                                      )}
                                      {item.notes && <span className="schedule-note">{item.notes}</span>}
                                    </div>
                                  </div>
                                  <div className="schedule-item-actions-row">
                                    <span className={`schedule-status ${item.status || 'assigned'}`}>
                                      {item.status === 'completed' ? '✅ Done' :
                                       item.status === 'in_progress' ? '🏃 In Progress' :
                                       item.status === 'skipped' ? '⏭ Skipped' :
                                       item.status === 'scheduled' ? '📅 Scheduled' : '📋 Assigned'}
                                    </span>
                                    {item.status !== 'completed' && (
                                      <button
                                        className="unassign-btn"
                                        onClick={() => handleUnassign(client._id, item._id)}
                                        title="Unassign"
                                      >
                                        <FaTrash />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Workout Session History */}
                          <h5 className="manage-section-title"><FaRunning /> Recent Workout Sessions</h5>
                          {clientSessions.length === 0 ? (
                            <p className="manage-empty">No workout sessions recorded yet.</p>
                          ) : (
                            <div className="manage-session-list">
                              {clientSessions.slice(0, 10).map(session => (
                                <div key={session._id} className="manage-session-item">
                                  <div className="manage-session-main">
                                    <strong>{session.workoutName || 'Workout'}</strong>
                                    <span className="manage-session-date">
                                      {new Date(session.startedAt || session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  </div>
                                  <div className="manage-session-stats">
                                    <span className={`manage-session-status ${session.status}`}>
                                      {session.status === 'completed' ? '✅' : session.status === 'in_progress' ? '🏃' : '❌'} {session.status}
                                    </span>
                                    {session.durationMinutes > 0 && (
                                      <span>{session.durationMinutes < 60 ? `${Math.round(session.durationMinutes)}m` : `${Math.floor(session.durationMinutes / 60)}h ${Math.round(session.durationMinutes % 60)}m`}</span>
                                    )}
                                    {session.exercisesCompleted > 0 && <span>{session.exercisesCompleted} exercises</span>}
                                    {session.rating > 0 && <span>{'★'.repeat(session.rating)}</span>}
                                    {session.perceivedDifficulty && <span className="manage-session-difficulty">{session.perceivedDifficulty.replace(/_/g, ' ')}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Nutrition Plan Status */}
                          <h5 className="manage-section-title"><FaUtensils /> Nutrition Plans</h5>
                          {clientNutritionPlans.length === 0 ? (
                            <p className="manage-empty">No nutrition plans for this client.</p>
                          ) : (
                            <div className="manage-nutrition-list">
                              {clientNutritionPlans.map(plan => (
                                <div key={plan._id} className="manage-nutrition-item">
                                  <div className="manage-nutrition-main">
                                    <strong>{plan.name}</strong>
                                    {plan.isActive && <span className="manage-nutrition-active">Active</span>}
                                  </div>
                                  <div className="manage-nutrition-macros">
                                    <span>{plan.dailyCalories} cal</span>
                                    <span>{plan.dailyProtein}g P</span>
                                    <span>{plan.dailyCarbs}g C</span>
                                    <span>{plan.dailyFat}g F</span>
                                  </div>
                                  <span className="manage-nutrition-date">
                                    Updated {new Date(plan.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="content-card-actions">
                      <button title="View Client Info" onClick={() => setViewingClientInfo(viewingClientInfo === client._id ? null : client._id)} className={viewingClientInfo === client._id ? 'active' : ''}>
                        <FaInfoCircle />
                      </button>
                      {client.status === 'active' && (
                        <button
                          title="Message Client"
                          onClick={() => setMessagingClient(messagingClient === client._id ? null : client._id)}
                          className={`chat-icon-btn ${messagingClient === client._id ? 'active' : ''}`}
                        >
                          <FaCommentDots />
                          {unreadCounts[client._id] > 0 && (
                            <span className="chat-unread-badge">{unreadCounts[client._id]}</span>
                          )}
                        </button>
                      )}
                      {(client.coachType === 'nutrition' || client.coachType === 'both') && client.status !== 'pending' && (
                        client.activeNutritionGoalId ? (
                          <button
                            type="button"
                            title="Edit nutrition plan"
                            className={nutritionPlannerClient?.clientId === client._id ? 'active' : ''}
                            onClick={() => {
                              const gId = typeof client.activeNutritionGoalId === 'object'
                                ? client.activeNutritionGoalId._id
                                : client.activeNutritionGoalId;
                              setNutritionPlannerClient(
                                nutritionPlannerClient?.clientId === client._id
                                  ? null
                                  : { clientId: client._id, goalId: gId }
                              );
                            }}
                          >
                            <FaUtensils />
                          </button>
                        ) : (
                          <button
                            type="button"
                            title="Create nutrition plan"
                            className={nutritionPlannerClient?.clientId === client._id ? 'active' : ''}
                            onClick={() => setNutritionPlannerClient(
                              nutritionPlannerClient?.clientId === client._id
                                ? null
                                : { clientId: client._id, goalId: null }
                            )}
                          >
                            <FaUtensils />
                          </button>
                        )
                      )}
                      {client.status === 'active' && (
                        <button title="Manage Client" onClick={() => handleManageClient(client._id)} className={managingClient === client._id ? 'active' : ''}>
                          <FaCog />
                        </button>
                      )}
                      {client.status === 'active' && (
                        <button title="Assign Program" onClick={() => setAssigningClient(assigningClient === client._id ? null : client._id)}>
                          <FaPlus />
                        </button>
                      )}
                      {client.status === 'invited' && client.inviteToken && (
                        <button title="Copy Invite Link" onClick={() => copyInviteLink(client)}>
                          <FaCopy />
                        </button>
                      )}
                      {client.status === 'invited' && (
                        <button title="Resend Invite" onClick={() => handleResendInvite(client._id)}>
                          <FaLink />
                        </button>
                      )}
                      <button title="Remove Client" className="danger" onClick={() => handleDeleteClient(client._id, client.clientName)}>
                        <FaTrash />
                      </button>
                    </div>
                    {client.status === 'active' && messagingClient === client._id && (
                      <ChatPanel
                        coachClientId={client._id}
                        currentUserId={currentUser?.id}
                        otherName={client.clientName || client.clientEmail || 'Client'}
                        otherPicture={null}
                        onClose={() => {
                          setMessagingClient(null);
                          refreshUnreadCounts();
                        }}
                      />
                    )}
                    {nutritionPlannerClient?.clientId === client._id && (
                      <div className="coach-nutrition-embed">
                        <NutritionPlannerWizard
                          embedded
                          propClientId={nutritionPlannerClient.clientId}
                          propGoalId={nutritionPlannerClient.goalId}
                          onClose={() => {
                            setNutritionPlannerClient(null);
                            handleManageClient(client._id);
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>Service Packages</h2>
              <button className="service-header-btn" onClick={() => { resetServiceForm(); setShowServiceForm(!showServiceForm); }}>
                <FaPlus /> {showServiceForm ? 'Cancel' : 'Create Service'}
              </button>
            </div>

            {(!stripeStatus || !stripeStatus.chargesEnabled) && (
              <div className="setup-guide">
                <h3><i className="fas fa-info-circle"></i> Getting Started with Services</h3>
                <p>Before clients can subscribe to your services, you need to set up payments.</p>
                <ol>
                  <li><strong>Go to the Payments tab</strong> and connect your Stripe account. This only takes a few minutes.</li>
                  <li><strong>Come back here</strong> and create a service package with your pricing, features, and session details.</li>
                  <li><strong>Clients connect with you</strong> from your coach profile page. You'll see their requests in the Clients tab — approve them to let them subscribe.</li>
                  <li><strong>Once connected and approved</strong>, clients can subscribe & pay for your services directly through your page.</li>
                </ol>
                <button className="setup-guide-btn" onClick={() => setActiveTab('payments')}>
                  <FaCreditCard /> Set Up Payments
                </button>
              </div>
            )}

            {stripeStatus?.chargesEnabled && services.length === 0 && !showServiceForm && (
              <div className="setup-guide success">
                <h3><i className="fas fa-check-circle"></i> Payments Connected!</h3>
                <p>You're ready to create your first service. Define what you offer, set your price, and clients who connect with you can subscribe right from your coach page.</p>
              </div>
            )}

            {showServiceForm && (
              <form ref={serviceFormRef} className="service-builder" onSubmit={handleSaveService}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Service Name *</label>
                    <input type="text" value={serviceForm.name} onChange={e => setServiceForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Premium Coaching" required />
                  </div>
                  <div className="form-group">
                    <label>Tier</label>
                    <select value={serviceForm.tier} onChange={e => setServiceForm(p => ({ ...p, tier: e.target.value }))}>
                      <option value="basic">Basic</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea value={serviceForm.description} onChange={e => setServiceForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe what's included..." rows={2} />
                  </div>
                </div>

                <div className="form-section-label">Sessions</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Frequency</label>
                    <select value={serviceForm.sessionFrequency} onChange={e => setServiceForm(p => ({ ...p, sessionFrequency: e.target.value }))}>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Biweekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="none">No sessions</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Duration (min)</label>
                    <select value={serviceForm.sessionDuration} onChange={e => setServiceForm(p => ({ ...p, sessionDuration: Number(e.target.value) }))}>
                      {[30, 45, 60, 75, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Session Type</label>
                    <select value={serviceForm.sessionType} onChange={e => setServiceForm(p => ({ ...p, sessionType: e.target.value }))}>
                      <option value="in_person">In Person</option>
                      <option value="virtual">Virtual</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                </div>

                <div className="form-section-label">Features</div>
                {serviceForm.features.map((feat, i) => (
                  <div key={i} className="feature-row">
                    <input type="text" value={feat} onChange={e => updateServiceFeature(i, e.target.value)} placeholder="e.g. Custom workout plan" />
                    {serviceForm.features.length > 1 && (
                      <button type="button" className="remove-btn" onClick={() => removeServiceFeature(i)}><FaTimes /></button>
                    )}
                  </div>
                ))}
                <button type="button" className="link-btn" onClick={addServiceFeature}><FaPlus /> Add Feature</button>

                <div className="form-section-label">Pricing & Visibility</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Price (USD) *</label>
                    <input type="number" value={serviceForm.price} onChange={e => setServiceForm(p => ({ ...p, price: e.target.value }))} placeholder="49.99" min="0" step="0.01" required />
                  </div>
                  <div className="form-group">
                    <label>Billing</label>
                    <select value={serviceForm.billingInterval} onChange={e => setServiceForm(p => ({ ...p, billingInterval: e.target.value }))}>
                      <option value="monthly">Monthly recurring</option>
                      <option value="one_time">One-time</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Visibility</label>
                    <select value={serviceForm.visibility} onChange={e => setServiceForm(p => ({ ...p, visibility: e.target.value }))}>
                      <option value="public">Public</option>
                      <option value="invite_only">Invite Only</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Max Clients (0 = unlimited)</label>
                    <input type="number" value={serviceForm.maxClients} onChange={e => setServiceForm(p => ({ ...p, maxClients: Number(e.target.value) }))} min="0" />
                  </div>
                </div>

                {programs.length > 0 && (
                  <>
                    <div className="form-section-label">Linked Programs</div>
                    <div className="checkbox-list">
                      <label className="checkbox-item">
                        <input type="checkbox" checked={serviceForm.includesCustomProgram} onChange={e => setServiceForm(p => ({ ...p, includesCustomProgram: e.target.checked }))} />
                        Includes custom program creation
                      </label>
                      {programs.map(prog => (
                        <label key={prog._id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={serviceForm.linkedPrograms.includes(prog._id)}
                            onChange={e => {
                              setServiceForm(p => ({
                                ...p,
                                linkedPrograms: e.target.checked
                                  ? [...p.linkedPrograms, prog._id]
                                  : p.linkedPrograms.filter(id => id !== prog._id)
                              }));
                            }}
                          />
                          {prog.name}
                        </label>
                      ))}
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button type="submit" className="service-form-save-btn" disabled={savingService}>
                    {savingService ? 'Saving...' : editingService ? 'Update Service' : 'Create Service'}
                  </button>
                </div>
              </form>
            )}

            {services.length === 0 && !showServiceForm ? (
              <div className="empty-state">
                <FaBoxOpen />
                <p>No services yet. Create your first service package!</p>
                <button onClick={() => setShowServiceForm(true)}><FaPlus /> Create Service</button>
              </div>
            ) : (
              <div className="content-list">
                {services.map(service => (
                  <div key={service._id} className="content-card service-card">
                    <div className="content-card-main">
                      <h4>{service.name}</h4>
                      {service.description && <p className="service-desc">{service.description}</p>}
                      <div className="content-card-meta">
                        <span className="price-badge">${(service.price / 100).toFixed(2)}/{service.billingInterval === 'monthly' ? 'mo' : 'once'}</span>
                        <span className="tier-badge">{service.tier}</span>
                        {service.sessionFrequency !== 'none' && (
                          <span>{service.sessionFrequency} {service.sessionType === 'both' ? '' : service.sessionType?.replace('_', ' ')} sessions ({service.sessionDuration}min)</span>
                        )}
                        {service.visibility === 'invite_only' && <span className="access-badge invite-only">Invite Only</span>}
                        <span>{service.currentClientCount}{service.maxClients > 0 ? `/${service.maxClients}` : ''} clients</span>
                      </div>
                      {service.features?.length > 0 && (
                        <div className="service-features">
                          {service.features.map((f, i) => <span key={i} className="feature-tag">✓ {f}</span>)}
                        </div>
                      )}
                    </div>
                    <div className="content-card-actions">
                      <button title="Edit" onClick={() => handleEditService(service)}><FaEdit /></button>
                      <button title="Delete" className="danger" onClick={() => handleDeleteService(service._id)}><FaTrash /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="tab-content">
            <section className="usage-tab-section">
              <div className="usage-tab-header">
                <h2><FaTachometerAlt /> Plan Usage</h2>
                {subscriptionInfo?.affiliate?.tier && (
                  <span className="usage-tier-badge">
                    {subscriptionInfo.affiliate.tier === 'pro' ? 'Pro' : 'Free'} Plan
                  </span>
                )}
              </div>

              <div className="usage-table-wrapper">
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th>Resource</th>
                      <th>Used</th>
                      <th>Limit</th>
                      <th>Remaining</th>
                      <th>Usage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="usage-section-row"><td colSpan="5"><strong>Fitness Content</strong></td></tr>
                    <tr>
                      <td className="usage-resource"><FaDumbbell /> Workouts</td>
                      <td>{workouts.length}</td>
                      <td>{usageStats?.limits?.maxWorkouts || '—'}</td>
                      <td>{usageStats?.limits?.maxWorkouts ? Math.max(0, usageStats.limits.maxWorkouts - workouts.length) : '—'}</td>
                      <td>
                        <div className="usage-bar">
                          <div className="usage-bar-fill" style={{ width: `${usageStats?.limits?.maxWorkouts ? Math.min(100, (workouts.length / usageStats.limits.maxWorkouts) * 100) : 0}%` }} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="usage-resource"><FaCalendarAlt /> Programs</td>
                      <td>{programs.length}</td>
                      <td>{usageStats?.limits?.maxPrograms || '—'}</td>
                      <td>{usageStats?.limits?.maxPrograms ? Math.max(0, usageStats.limits.maxPrograms - programs.length) : '—'}</td>
                      <td>
                        <div className="usage-bar">
                          <div className="usage-bar-fill" style={{ width: `${usageStats?.limits?.maxPrograms ? Math.min(100, (programs.length / usageStats.limits.maxPrograms) * 100) : 0}%` }} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="usage-resource"><FaRunning /> Exercises</td>
                      <td>{exercises.length}</td>
                      <td>{usageStats?.limits?.maxExercises || '—'}</td>
                      <td>{usageStats?.limits?.maxExercises ? Math.max(0, usageStats.limits.maxExercises - exercises.length) : '—'}</td>
                      <td>
                        <div className="usage-bar">
                          <div className="usage-bar-fill" style={{ width: `${usageStats?.limits?.maxExercises ? Math.min(100, (exercises.length / usageStats.limits.maxExercises) * 100) : 0}%` }} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="usage-resource"><FaUsers /> Fitness/Nutrition Clients</td>
                      <td>{clients.filter(c => c.status !== 'pending').length}</td>
                      <td>{usageStats?.limits?.maxFitnessClients || '—'}</td>
                      <td>{usageStats?.limits?.maxFitnessClients ? Math.max(0, usageStats.limits.maxFitnessClients - clients.filter(c => c.status !== 'pending').length) : '—'}</td>
                      <td>
                        <div className="usage-bar">
                          <div className="usage-bar-fill" style={{ width: `${usageStats?.limits?.maxFitnessClients ? Math.min(100, (clients.filter(c => c.status !== 'pending').length / usageStats.limits.maxFitnessClients) * 100) : 0}%` }} />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="usage-info-note">
                <i className="fas fa-info-circle"></i>
                <p>Daily limits reset at midnight UTC. Monthly limits reset on the 1st of each month.</p>
              </div>
            </section>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>Payment Settings</h2>
            </div>

            <div className="stripe-section">
              {!stripeStatus || !stripeStatus.connected ? (
                <div className="stripe-card">
                  <div className="stripe-icon">💳</div>
                  <h3>Connect Your Stripe Account</h3>
                  <p>Use Stripe Connect to accept client payments for coaching. FreshFitLab platform subscriptions stay separate from your coaching payouts.</p>
                  <button className="stripe-btn" onClick={handleStripeOnboard} disabled={stripeLoading}>
                    {stripeLoading ? 'Connecting...' : 'Connect with Stripe'}
                  </button>
                  {process.env.NODE_ENV !== 'production' && (
                    <button className="stripe-btn outline" onClick={handleDevStripeOnboard} disabled={stripeLoading} style={{ marginTop: '0.5rem' }}>
                      {stripeLoading ? 'Creating...' : '⚡ Quick Test Setup (Dev Only)'}
                    </button>
                  )}
                </div>
              ) : !stripeStatus.onboarded ? (
                <div className="stripe-card pending">
                  <div className="stripe-icon">⏳</div>
                  <h3>Complete Your Stripe Setup</h3>
                  <p>{stripeAccountTypeLabel} connected. Finish onboarding so Stripe can enable client charges and payouts to your bank account.</p>
                  <div className="stripe-actions">
                    <button className="stripe-btn" onClick={handleStripeOnboard} disabled={stripeLoading}>
                      {stripeLoading ? 'Loading...' : 'Continue Setup'}
                    </button>
                    <button className="stripe-btn outline" onClick={handleStripeRefresh} disabled={stripeLoading}>
                      Refresh Status
                    </button>
                  </div>
                  {stripeActionItems.length > 0 && (
                    <div className="stripe-requirements">
                      <h4>Stripe still needs</h4>
                      <ul>
                        {stripeActionItems.slice(0, 6).map(item => (
                          <li key={item}>{item.replaceAll('_', ' ')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="stripe-card connected">
                  <div className="stripe-icon">✅</div>
                  <h3>Stripe Connected</h3>
                  <p>
                    {stripeStatus.payoutsEnabled
                      ? 'Your account is fully set up and ready to receive payments!'
                      : 'Your account is connected. Payouts will be enabled once Stripe verifies your information.'}
                  </p>
                  <div className="stripe-badges">
                    <span className={`stripe-badge ${stripeStatus.chargesEnabled ? 'active' : ''}`}>
                      {stripeStatus.chargesEnabled ? '✓' : '⏳'} Charges
                    </span>
                    <span className={`stripe-badge ${stripeStatus.payoutsEnabled ? 'active' : ''}`}>
                      {stripeStatus.payoutsEnabled ? '✓' : '⏳'} Payouts
                    </span>
                    <span className="stripe-badge active subtle">
                      {stripeAccountTypeLabel}
                    </span>
                  </div>
                  <div className="stripe-actions">
                    <button className="stripe-btn outline" onClick={handleStripeDashboard}>
                      Open Stripe Dashboard
                    </button>
                    <button className="stripe-btn outline" onClick={handleStripeRefresh} disabled={stripeLoading}>
                      Refresh Status
                    </button>
                  </div>
                  {(stripeActionItems.length > 0 || stripeStatus.disabledReason) && (
                    <div className="stripe-requirements compact">
                      <h4>Remaining Stripe follow-up</h4>
                      {stripeStatus.disabledReason && (
                        <p className="stripe-disabled-reason">Reason: {stripeStatus.disabledReason.replaceAll('_', ' ')}</p>
                      )}
                      {stripeActionItems.length > 0 && (
                        <ul>
                          {stripeActionItems.slice(0, 4).map(item => (
                            <li key={item}>{item.replaceAll('_', ' ')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="stripe-info">
                <p>FreshFitLab subscriptions are billed by the platform. Stripe Connect is only for coach-client payments, and those payouts route to the connected coach account.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        danger={confirmModal.danger}
        confirmLabel={confirmModal.confirmLabel}
        onConfirm={() => closeConfirm(true)}
        onCancel={() => closeConfirm(false)}
      />
    </div>
  );
}
